import { toast } from "react-hot-toast";

import { isAxiosError } from "axios";

import { useQueryClient } from "@tanstack/react-query";

import {
	chatbotControllerGetMessagesV1,
	chatbotControllerQueryV1,
	type ChatbotDto,
	type ChatbotMessageDto,
	getChatbotControllerGetMessagesV1QueryKey,
} from "@virtual-brain-lib/frontend-api";
import { useSentry } from "@virtual-brain-lib/frontend-utils";
import type {
	AnswerError,
	ChatMessage,
	ChatMessageWithUIState,
	MessageSource,
} from "@virtual-brain-lib/types";

import { abortChatStream } from "./abortChatStream";
import type { ChatMessagesPageData } from "./useChatMessagesCache";
import { useFetchChatMessages } from "./useFetchChatMessages";
import { useSendMessage } from "./useSendMessage";

function messageDtoToChatMessage(message: ChatbotMessageDto): ChatMessage {
	return {
		id: message.id,
		role: message.role,
		content: message.content,
		rawContent: message.rawContent,
		thinkingContent: message.thinkingContent,
		suggestedPrompts: message.suggestedPrompts,
		status: message.status,
		// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
		sources: message.sources as MessageSource[] | undefined,
		createdAt: new Date(message.createdAt),
		isFollowUp: false,
		webMode: "auto",
	};
}

interface UseChatbotMessagesParams {
	chatbot: ChatbotDto;
	chatbotSessionId?: string;
}

export function useChatbotMessages({ chatbot, chatbotSessionId = "" }: UseChatbotMessagesParams) {
	const { captureException } = useSentry();
	const queryClient = useQueryClient();

	const queryKey = [...getChatbotControllerGetMessagesV1QueryKey(chatbot.id), chatbotSessionId];

	function handleError(error: unknown) {
		if (error instanceof Error) {
			captureException(error);
		}

		const errorMessage =
			(isAxiosError<AnswerError>(error) ? error.response?.data?.message : undefined) ??
			"We are really sorry, our servers are overloaded and we could not process your request. Please try again.";

		toast.error(errorMessage);
	}

	async function fetchMessages() {
		try {
			const response = await chatbotControllerGetMessagesV1(chatbot.id);

			return response.map(messageDtoToChatMessage);
		} catch (error) {
			handleError(error);
			throw error;
		}
	}

	async function sendMessageFn(params: { query: string }) {
		const result = await chatbotControllerQueryV1(chatbot.id, {
			query: params.query,
		});

		return {
			userMessage: messageDtoToChatMessage(result.userMessage),
			assistantMessage: messageDtoToChatMessage(result.assistantMessage),
		};
	}

	const {
		messages,
		isLoading,
		isPlaceholderData,
		fetchMoreMessages,
		hasMoreMessages,
		isFetchingMoreMessages,
	} = useFetchChatMessages({
		chatId: chatbotSessionId,
		queryKey,
		fetchMessages,
	});
	const { sendMessage: sendMessageInternal, isSending } = useSendMessage<{ query: string }>({
		queryKey,
		sendMessageFn,
	});

	async function sendMessage(query: string) {
		await sendMessageInternal({ query });
	}

	function updateCachedMessage({
		messageId,
		updates,
	}: {
		messageId: ChatMessage["id"];
		updates: Partial<ChatMessageWithUIState>;
	}) {
		queryClient.setQueryData<ChatMessagesPageData>(queryKey, (oldData) => {
			const cachedMessages = oldData?.pages[0];

			if (!oldData || !cachedMessages) {
				return oldData;
			}

			return {
				...oldData,
				pages: [
					cachedMessages.map((message) =>
						message.id === messageId ? { ...message, ...updates } : message,
					),
				],
			};
		});
	}

	function abortStream(this: void): void {
		abortChatStream(chatbotSessionId);
	}

	return {
		messages,
		isLoading,
		isPlaceholderData,
		fetchMoreMessages,
		hasMoreMessages,
		isFetchingMoreMessages,
		isSending,
		sendMessage,
		updateCachedMessage,
		abortStream,
	};
}
